/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

import kotlinx.browser.window
import kotlinx.serialization.SerializationException
import kotlinx.serialization.json.Json
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.renderComposable
import org.w3c.dom.url.URLSearchParams
import systems.frictionless.analytics.Analytics
import systems.frictionless.analytics.fixture.NoopAnalytics
import systems.frictionless.analytics.internal.DefaultAnalyticsImpl
import systems.frictionless.analytics.model.AnalyticsConfig
import systems.frictionless.impulse.browser.BuildConfig
import systems.frictionless.impulse.browser.SharedIds
import systems.frictionless.impulse.browser.composable.MainView
import systems.frictionless.impulse.browser.routing.BrowserWindowObjectProxy
import systems.frictionless.impulse.browser.style.AppStyleSheet
import systems.frictionless.impulse.browser.viewmodel.RoutingViewModel
import systems.frictionless.impulse.rest.client.DefaultRestClient
import systems.frictionless.impulse.rest.client.RestClient
import systems.frictionless.impulse.viewmodel.WizardViewModel

/*
 * This provides an alternative entrypoint (besides Main), if the browserApp is treated as a jsMain gradle dependency by
 * serverApp.  We no longer do this, instead letting browserApp's output just be embedded in the final serverApp jar
 * as a servable resource.
 */
object Entrypoint {
    fun run() {
        val analytics: Analytics =
            run {
                if (BuildConfig.SENTRY_BROWSER_CONFIG.isBlank()) {
                    println("Set SENTRY_BROWSER_CONFIG to enable Sentry analytics")
                    NoopAnalytics()
                } else {
                    try {
                        val config = Json.decodeFromString<AnalyticsConfig>(BuildConfig.SENTRY_BROWSER_CONFIG)
                        DefaultAnalyticsImpl(config, BuildConfig.GIT_COMMIT_COUNT, BuildConfig.GIT_SHA)
                    } catch (e: SerializationException) {
                        error("Unable to parse analytics config")
                    } catch (e: IllegalArgumentException) {
                        error("Unable to parse analytics config")
                    }
                }
            }

        val restClient: RestClient =
            run {
                // https://github.com/ktorio/ktor/issues/1695
                val endpoint = window.location.origin
                // Note that the HTTP client is never closed, because it is used effectively for the lifetime of the application
                val httpClient = DefaultRestClient.newHttpClient(endpoint)
                DefaultRestClient(httpClient)
            }

        val routingViewModel = RoutingViewModel(BrowserWindowObjectProxy(window))

        checkForCrashInjectionTest(routingViewModel.initialQueryString, analytics)

        val wizardViewModel = WizardViewModel(restClient)
        renderComposable(rootElementId = SharedIds.ROOT_HTML_ELEMENT) {
            Style(AppStyleSheet)
            MainView(restClient, routingViewModel, wizardViewModel, analytics)
        }
    }
}

/**
 * Special debug query string, with possible values of "none", "caught", "uncaught".
 */
private const val QUERY_STRING_KEY_EXCEPTION = "exception"

/*
 * This allows QA verification of crash analytics integration
 */
@Suppress("TooGenericExceptionThrown")
private fun checkForCrashInjectionTest(
    queryString: String,
    analytics: Analytics
) {
    when (CrashMode.fromUnsafeString(URLSearchParams(queryString).get(QUERY_STRING_KEY_EXCEPTION))) {
        CrashMode.None -> {
            // Do nothing
        }
        CrashMode.Caught -> {
            analytics.crashAnalytics.reportCaughtException(Exception("Simulated crash"))
        }
        CrashMode.Uncaught -> {
            throw RuntimeException("Debug uncaught exception")
        }
    }
}

private enum class CrashMode {
    None,
    Caught,
    Uncaught;

    companion object {
        fun fromUnsafeString(string: String?) =
            when (string) {
                "none" -> None
                "caught" -> Caught
                "uncaught" -> Uncaught
                else -> None
            }
    }
}
